import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { listTerritories, type TerritoryRow } from '../../api/orgApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { painterStatusLabel } from '../../../../shared/contracts/painter-status.js';
import {
  M08_PAINTER_ASSIGNMENT_STATUSES,
  type M08PainterAssignmentStatus,
} from '../../../../shared/contracts/backend-enums.js';

interface PainterAssignmentRow {
  readonly painterAssignmentId: string;
  readonly jobNumber: string | null;
  readonly customerName: string;
  readonly territoryId: string;
  readonly territoryName: string | null;
  readonly painterUserId: string;
  readonly painterName: string | null;
  readonly status: string;
  readonly expectedEndDate: string;
  readonly areaRequested: number;
  readonly areaUnit: string;
}

interface PainterAssignmentList {
  readonly items: readonly PainterAssignmentRow[];
}

// PainterAssignmentStatus (T096); ASSIGNED / IN_PROGRESS were never members.
const STATUSES = ['ALL', ...M08_PAINTER_ASSIGNMENT_STATUSES] as const;
type StatusFilter = 'ALL' | M08PainterAssignmentStatus;

export function PainterAssignmentsAdminPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const [rows, setRows] = useState<readonly PainterAssignmentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [territory, setTerritory] = useState('');
  // The filter takes a territory id. A free-text box asked the reader to type
  // a uuid, which nobody can do, so the territories are offered by name.
  const [territories, setTerritories] = useState<readonly TerritoryRow[]>([]);
  const [territoryError, setTerritoryError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listTerritories(token)
      .then((r) => { if (alive) { setTerritories(r.data); setTerritoryError(null); } })
      .catch((e) => {
        // Said, not swallowed: an empty picker and a picker that failed to
        // load look identical, and the reader would blame the data.
        if (alive) setTerritoryError((e as Error).message);
      });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    const params = new URLSearchParams();
    if (status !== 'ALL') params.set('status', status);
    if (territory) params.set('territoryId', territory);
    apiRequest<PainterAssignmentList>(`/v1/painter/assignments/admin?${params.toString()}`, { token })
      .then((r) => { if (alive) setRows(r?.items ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, status, territory]);

  if (error) {
    return (
      <main aria-label="painter-admin-list">
        <p role="alert">{error}</p>
      </main>
    );
  }
  if (!rows) {
    return (
      <main aria-label="painter-admin-list">
        <p aria-label="loading">{t('common.loading')}</p>
      </main>
    );
  }

  return (
    <main aria-label="painter-admin-list">
      <h1>{t('painter.admin.title')}</h1>
      <div>
        <label htmlFor="status-filter">{t('painter.admin.status')}</label>
        <select
          id="status-filter"
          aria-label="status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s === 'ALL' ? t('mob.painter.tasks.filter.ALL') : painterStatusLabel(t, s)}</option>
          ))}
        </select>
        <label htmlFor="territory-filter">{t('painter.admin.territory')}</label>
        <select
          id="territory-filter"
          aria-label="territory-filter"
          value={territory}
          onChange={(e) => setTerritory(e.target.value)}
        >
          <option value="">{t('painter.admin.territory.all')}</option>
          {territories.map((tr) => (
            <option key={tr.territoryId} value={tr.territoryId}>
              {tr.territoryName} ({tr.territoryCode})
            </option>
          ))}
        </select>
        {territoryError ? (
          <span role="alert" data-testid="territory-load-error">{territoryError}</span>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p aria-label="empty">{t('painter.admin.empty')}</p>
      ) : (
        <table aria-label="painter-admin-table">
          <thead>
            <tr>
              <th>{t('painter.admin.col.job')}</th>
              <th>{t('painter.admin.col.customer')}</th>
              <th>{t('painter.admin.col.painter')}</th>
              <th>{t('painter.admin.status')}</th>
              <th>{t('painter.admin.territory')}</th>
              <th>{t('painter.admin.col.area')}</th>
              <th>{t('painter.admin.col.due')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.painterAssignmentId} aria-label={`row-${r.painterAssignmentId}`}>
                <td>{r.jobNumber ?? '—'}</td>
                <td>{r.customerName || '—'}</td>
                <td>{r.painterName ?? '—'}</td>
                <td>{painterStatusLabel(t, r.status)}</td>
                <td>{r.territoryName ?? '—'}</td>
                <td>{decimal(r.areaRequested)} {unitLabel(r.areaUnit)}</td>
                <td>{day(r.expectedEndDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
